import Swal from 'sweetalert2';
import {
    FetchS3FileBlob,
    GetS3ImageBase64,
    GetS3PresignedDownloadUrls,
    GetS3ThumbnailBase64,
    S3FileItem,
    fetchBlobFromPresignedUrl,
    getFileNameFromKey,
    getFolderNameFromPrefix,
    openS3ImageInNewTab,
    pickOrCreateDownloadSubfolder,
    canUseDirectoryPicker,
    saveBlobAsFile,
    saveBlobToDirectory,
} from '../../../services/query/aiPrompt/S3BrowserQuery.services';

export const DOWNLOAD_CONCURRENCY = 4;
export const DOWNLOAD_MAX_ATTEMPTS = 3;

export enum DownloadItemStatus {
    Pending = 'Pending',
    Downloading = 'Downloading',
    Completed = 'Completed',
    Failed = 'Failed',
}

export interface DownloadQueueItem {
    id: string;
    key: string;
    fileName: string;
    size: number;
    progress: number;
    status: DownloadItemStatus;
    error?: string;
    attempt: number;
    maxAttempts: number;
    presignedUrl?: string;
}

export function formatDownloadDuration(ms: number): string {
    if (!Number.isFinite(ms) || ms < 0) return '0:00';
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) {
        return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${m}:${String(s).padStart(2, '0')}`;
}

export const formatBytes = (n: number) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const isValidFolderSegment = (name: string) => {
    const s = name.trim();
    if (!s || s.length > 255) return false;
    if (s === '.' || s === '..') return false;
    if (s.includes('/') || s.includes('\\') || /[\0\r\n]/.test(s)) return false;
    return true;
};

export const trimTrailingSlashes = (value: string): string => {
    let end = value.length;
    while (end > 0 && value[end - 1] === '/') {
        end -= 1;
    }
    return end === value.length ? value : value.slice(0, end);
};

export const isPreviewableImageKey = (key: string) => /\.(jpe?g|png|gif|webp)$/i.test(key);

export const canOpenFile = (file: S3FileItem) => Boolean(file.url) || isPreviewableImageKey(file.key);

export function dedupeByKey<T extends { key: string }>(items: T[]): T[] {
    const seen = new Set<string>();
    return items.filter((item) => {
        if (seen.has(item.key)) return false;
        seen.add(item.key);
        return true;
    });
}

export function mergeListItemsByKey<T extends { key: string }>(
    prev: T[],
    incoming: T[] | undefined,
    append: boolean
): T[] {
    const next = append ? [...prev, ...(incoming || [])] : [...(incoming || [])];
    return dedupeByKey(next);
}

export type OpenS3BrowserFileParams = {
    file: S3FileItem;
    showPreviewPanel: boolean;
    onFileSelect?: (file: S3FileItem) => void;
    downloadErrorTitle: string;
};

export async function openS3BrowserFile(params: OpenS3BrowserFileParams): Promise<void> {
    if (params.onFileSelect) {
        params.onFileSelect(params.file);
        return;
    }

    if (!params.showPreviewPanel) {
        return;
    }

    if (isPreviewableImageKey(params.file.key)) {
        try {
            await openS3ImageInNewTab(params.file.key);
        } catch {
            await Swal.fire({ icon: 'error', title: params.downloadErrorTitle });
        }
        return;
    }

    if (params.file.url) {
        window.open(params.file.url, '_blank', 'noopener,noreferrer');
    }
}

export type S3ImageSourceLoadResult = {
    src: string;
    objectUrl?: string;
};

export type S3ImageSourceOptions = {
    preferThumbnail?: boolean;
    skipErrorModal?: boolean;
};

export async function loadS3ImageSource(
    file: Pick<S3FileItem, 'key' | 'url' | 'thumbnailKey'>,
    options: S3ImageSourceOptions = {}
): Promise<S3ImageSourceLoadResult | null> {
    const { preferThumbnail = true, skipErrorModal = true } = options;

    if (file.url) {
        return { src: file.url };
    }

    if (preferThumbnail && file.thumbnailKey) {
        try {
            const { base64, contentType } = await GetS3ThumbnailBase64(file.thumbnailKey);
            if (base64) {
                return { src: `data:${contentType || 'image/jpeg'};base64,${base64}` };
            }
        } catch {
            /* thumbnail fallback */
        }
    }

    try {
        const { base64, contentType } = await GetS3ImageBase64(file.key, { skipErrorModal });
        if (base64) {
            return { src: `data:${contentType || 'image/jpeg'};base64,${base64}` };
        }
    } catch {
        /* blob fallback */
    }

    try {
        const blob = await FetchS3FileBlob(file.key, true, undefined, undefined, { skipErrorModal });
        const objectUrl = URL.createObjectURL(blob);
        return { src: objectUrl, objectUrl };
    } catch {
        return null;
    }
}

export async function loadS3FullImageSource(
    file: Pick<S3FileItem, 'key' | 'url'>,
    options: { skipErrorModal?: boolean } = {}
): Promise<S3ImageSourceLoadResult | null> {
    return loadS3ImageSource(file, { preferThumbnail: false, skipErrorModal: options.skipErrorModal ?? true });
}

export function calcDownloadPercent(loaded: number, total: number | null | undefined, fallbackSize: number): number {
    const basis = total && total > 0 ? total : fallbackSize > 0 ? fallbackSize : loaded;
    return basis > 0 ? Math.min(100, Math.round((loaded * 100) / basis)) : 0;
}

export function createInitialDownloadItems(keysWithSizes: { key: string; size: number }[]): DownloadQueueItem[] {
    return keysWithSizes.map((entry, index) => ({
        id: `${entry.key}::${index}`,
        key: entry.key,
        fileName: getFileNameFromKey(entry.key),
        size: entry.size,
        progress: 0,
        status: DownloadItemStatus.Pending,
        attempt: 0,
        maxAttempts: DOWNLOAD_MAX_ATTEMPTS,
    }));
}

export function mergeRefreshedDownloadItems(
    prev: DownloadQueueItem[],
    refreshed: DownloadQueueItem[]
): DownloadQueueItem[] {
    const refreshedById = new Map(refreshed.map((item) => [item.id, item]));
    return prev.map((item) => refreshedById.get(item.id) ?? item);
}

export type DownloadItemUpdater = (id: string, patch: Partial<DownloadQueueItem>) => void;

export type DownloadBlobContext = {
    preferPresigned: boolean;
    presignedCorsAvailable: boolean | null;
    updateDownloadItem: DownloadItemUpdater;
    setPresignedCorsAvailable: (value: boolean) => void;
};

export async function fetchDownloadBlob(item: DownloadQueueItem, ctx: DownloadBlobContext): Promise<Blob> {
    let blob: Blob | null = null;

    if (ctx.preferPresigned && item.presignedUrl && ctx.presignedCorsAvailable !== false) {
        try {
            blob = await fetchBlobFromPresignedUrl(item.presignedUrl, (loaded, total) => {
                ctx.updateDownloadItem(item.id, {
                    progress: calcDownloadPercent(loaded, total, item.size),
                });
            });
            ctx.setPresignedCorsAvailable(true);
        } catch {
            ctx.setPresignedCorsAvailable(false);
        }
    }

    if (!blob) {
        blob = await FetchS3FileBlob(item.key, false, (evt: { loaded?: number; total?: number }) => {
            const loaded = evt.loaded ?? 0;
            const total = evt.total ?? item.size ?? 0;
            ctx.updateDownloadItem(item.id, {
                progress: calcDownloadPercent(loaded, total > 0 ? total : undefined, item.size),
            });
        });
    }

    return blob;
}

export type SaveDownloadBlobContext = {
    downloadTargetDir: FileSystemDirectoryHandle | null;
};

export async function saveDownloadBlob(blob: Blob, item: DownloadQueueItem, ctx: SaveDownloadBlobContext): Promise<void> {
    const fileName = item.fileName || getFileNameFromKey(item.key);
    if (ctx.downloadTargetDir) {
        await saveBlobToDirectory(ctx.downloadTargetDir, fileName, blob);
        return;
    }
    saveBlobAsFile(blob, fileName);
}

export async function processDownloadItemAttempts(
    item: DownloadQueueItem,
    blobCtx: DownloadBlobContext,
    saveCtx: SaveDownloadBlobContext,
    updateDownloadItem: DownloadItemUpdater
): Promise<void> {
    for (let attempt = item.attempt; attempt < item.maxAttempts; attempt += 1) {
        updateDownloadItem(item.id, {
            status: DownloadItemStatus.Downloading,
            progress: attempt === item.attempt ? item.progress : 0,
            attempt: attempt + 1,
            error: undefined,
        });

        try {
            const blob = await fetchDownloadBlob(item, blobCtx);
            await saveDownloadBlob(blob, item, saveCtx);
            updateDownloadItem(item.id, {
                status: DownloadItemStatus.Completed,
                progress: 100,
            });
            return;
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            if (attempt + 1 >= item.maxAttempts) {
                updateDownloadItem(item.id, {
                    status: DownloadItemStatus.Failed,
                    error: message,
                });
                return;
            }
            await new Promise((resolve) => setTimeout(resolve, 800));
        }
    }
}

export async function runParallelDownloadWorkers(
    items: DownloadQueueItem[],
    concurrency: number,
    processItem: (item: DownloadQueueItem) => Promise<void>
): Promise<void> {
    let nextIndex = 0;

    const runWorker = async () => {
        while (nextIndex < items.length) {
            const index = nextIndex++;
            const item = items[index];
            if (item) {
                await processItem(item);
            }
        }
    };

    await Promise.all(
        Array.from({ length: Math.min(concurrency, items.length) }, () => runWorker())
    );
}

export type DownloadFolderPickerMessages = {
    pickFolderTitle: string;
    pickFolderText: string;
    ok: string;
    folderCancelled: string;
    folderFallback: string;
    folderUnsupported: string;
};

export type DownloadFolderPickerResult =
    | { status: 'skipped' }
    | { status: 'cancelled' }
    | { status: 'ready'; directory: FileSystemDirectoryHandle | null };

export async function resolveDownloadTargetDirectory(
    keysCount: number,
    resolvedFolderName: string | undefined,
    messages: DownloadFolderPickerMessages
): Promise<DownloadFolderPickerResult> {
    if (keysCount <= 1 || !resolvedFolderName) {
        return { status: 'skipped' };
    }

    if (!canUseDirectoryPicker()) {
        await Swal.fire({
            icon: 'info',
            title: messages.folderUnsupported,
        });
        return { status: 'ready', directory: null };
    }

    try {
        await Swal.fire({
            icon: 'info',
            title: messages.pickFolderTitle,
            text: messages.pickFolderText,
            confirmButtonText: messages.ok,
        });
        const directory = await pickOrCreateDownloadSubfolder(resolvedFolderName);
        return { status: 'ready', directory };
    } catch (err) {
        const aborted =
            err instanceof DOMException && (err.name === 'AbortError' || err.name === 'NotAllowedError');
        if (aborted) {
            await Swal.fire({
                icon: 'info',
                title: messages.folderCancelled,
            });
            return { status: 'cancelled' };
        }

        await Swal.fire({
            icon: 'warning',
            title: messages.folderFallback,
        });
        return { status: 'ready', directory: null };
    }
}

export function resolveDownloadFolderName(
    folderName: string | undefined,
    keysCount: number,
    prefix: string,
    effectiveBasePrefix: string
): string | undefined {
    return folderName || (keysCount > 1 ? getFolderNameFromPrefix(prefix || effectiveBasePrefix) : undefined);
}

export async function attachPresignedUrlsToDownloadItems(
    initialItems: DownloadQueueItem[]
): Promise<DownloadQueueItem[]> {
    const presigned = await GetS3PresignedDownloadUrls(initialItems.map((item) => item.key));
    const urlByKey = new Map(presigned.items.map((entry) => [entry.key, entry]));
    return initialItems.map((item) => {
        const match = urlByKey.get(item.key);
        return {
            ...item,
            fileName: match?.fileName || item.fileName,
            presignedUrl: match?.url,
        };
    });
}

export function getDownloadStatusTextKey(status: DownloadItemStatus): string {
    switch (status) {
        case DownloadItemStatus.Downloading:
            return 'aiPrompt.s3Browser.downloadDownloading';
        case DownloadItemStatus.Completed:
            return 'aiPrompt.s3Browser.downloadCompleted';
        case DownloadItemStatus.Failed:
            return 'aiPrompt.s3Browser.downloadFailed';
        default:
            return 'aiPrompt.s3Browser.downloadReady';
    }
}

export function getDownloadChipColor(status: DownloadItemStatus): 'default' | 'primary' | 'success' | 'error' {
    switch (status) {
        case DownloadItemStatus.Downloading:
            return 'primary';
        case DownloadItemStatus.Completed:
            return 'success';
        case DownloadItemStatus.Failed:
            return 'error';
        default:
            return 'default';
    }
}

export function computeDisplayPath(prefix: string, environment: string): string {
    if (!prefix) return '';
    const envPrefix = trimTrailingSlashes(String(environment ?? '').trim());
    const baseToHide1 = envPrefix ? `${envPrefix}/igloo_share/` : 'igloo_share/';
    const baseToHide2 = envPrefix ? `${envPrefix}/siteye_yuklenecekler/` : 'siteye_yuklenecekler/';

    if (prefix.toLowerCase().startsWith(baseToHide1.toLowerCase())) {
        return prefix.substring(baseToHide1.length);
    }
    if (prefix.toLowerCase().startsWith(baseToHide2.toLowerCase())) {
        return prefix.substring(baseToHide2.length);
    }
    return prefix;
}
